import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 } from 'uuid';
import { configManager } from 'Helpers/config-manager';
import { securityClient } from 'securityClient';
import {
  ChatMessage,
  ChatEvent,
  ConversationDetail,
  ToolCallProgress,
} from 'Models/chat';

/** The server allocates turn sequences from 1 (`ADD last_seq :one` on a META seeded at 0). */
const FIRST_SEQ = 1;

function toolLabel(toolName: string, input: Record<string, unknown>): string {
  if (toolName === 'query_hs_objects') {
    return input.objectType === 'deals' ? 'Querying deals…' : 'Querying contacts…';
  }
  if (toolName === 'get_pipeline_stages') {
    return 'Looking up pipeline stages…';
  }
  if (toolName === 'get_pipelines') {
    return 'Looking up pipelines…';
  }
  // REV-200 Phase 4 shipped one insights tool, not the two this label list was
  // written against: comparison is a `compareTo` field on the same call rather
  // than a `get_insight_comparison` of its own. Both labels still apply — they
  // just key off the input, the way query_hs_objects already keys off
  // objectType — and a comparison is the slower of the two, so saying which
  // one is running is worth more here than on either of the lookups above.
  if (toolName === 'get_funnel_insights') {
    return input.compareTo ? 'Comparing time periods…' : 'Analyzing funnel performance…';
  }
  return `Running ${toolName}…`;
}

function applyEvent(
  prev: ChatMessage[],
  event: ChatEvent,
): ChatMessage[] {
  const last = prev[prev.length - 1];
  if (!last || last.role !== 'assistant') return prev;

  switch (event.type) {
    case 'tool_start': {
      const progress: ToolCallProgress = {
        toolName: event.toolName,
        label: toolLabel(event.toolName, event.input),
        status: 'running',
      };
      return [...prev.slice(0, -1), { ...last, toolCalls: [...last.toolCalls, progress] }];
    }
    case 'tool_end': {
      let matched = false;
      const toolCalls = last.toolCalls.map((tc) => {
        if (!matched && tc.toolName === event.toolName && tc.status === 'running') {
          matched = true;
          return { ...tc, status: 'done' as const, durationMs: event.durationMs };
        }
        return tc;
      });
      return [...prev.slice(0, -1), { ...last, toolCalls }];
    }
    case 'answer_chunk':
      return [...prev.slice(0, -1), { ...last, content: last.content + event.text }];
    case 'answer_done':
      return [...prev.slice(0, -1), { ...last, isStreaming: false }];
    case 'partial_answer':
      // Arrives *before* the first answer_chunk of this turn, so the marker is
      // on the message from the moment the answer starts rendering rather than
      // appearing under it once the stream ends.
      return [
        ...prev.slice(0, -1),
        {
          ...last,
          partial: {
            budget: event.budget,
            iterations: event.iterations,
            toolCalls: event.toolCalls,
          },
        },
      ];
    default:
      // An event type this client does not know about (10.4). Returning the
      // same array reference makes the setMessages call a no-op React bails
      // out of, so a future server-side addition costs a render, not a break.
      return prev;
  }
}

function markLastAssistantDone(prev: ChatMessage[]): ChatMessage[] {
  const last = prev[prev.length - 1];
  if (!last || last.role !== 'assistant') return prev;
  return [...prev.slice(0, -1), { ...last, isStreaming: false }];
}

export interface UseStreamingChatOptions {
  /**
   * Conversation to start attached to. Read once, on mount — afterwards the
   * hook owns the active id, since `conversation_started`, `loadConversation`
   * and `startNewConversation` all move it.
   */
  conversationId?: string;
  /**
   * Fired when the server creates a conversation for a turn that had no id.
   * The sidebar uses it to invalidate its listing. Does not need memoising.
   */
  onConversationCreated?: (conversationId: string) => void;
}

/**
 * Chat transport for the assistant page.
 *
 * Conversation history lives on the server (REV-201): a turn posts only the new
 * message plus the conversation it belongs to, and the backend assembles the
 * context. `messages` is therefore a render model, not the payload it used to be.
 */
export function useStreamingChat(options: UseStreamingChatOptions = {}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(
    options.conversationId ?? null,
  );
  const [compactedThroughSeq, setCompactedThroughSeq] = useState<number | null>(null);

  // The streaming loop is async and long-lived, so it reads the active id from
  // a ref rather than from the closure it was created in — a turn sent right
  // after `conversation_started` must carry the new id, not the null it saw.
  const conversationIdRef = useRef(conversationId);
  const applyConversationId = useCallback((id: string | null) => {
    conversationIdRef.current = id;
    setConversationId(id);
  }, []);

  const onCreatedRef = useRef(options.onConversationCreated);
  useEffect(() => {
    onCreatedRef.current = options.onConversationCreated;
  }, [options.onConversationCreated]);

  /**
   * Next sequence the server is expected to allocate. Turns hydrated from a
   * transcript carry their real seq; locally sent ones are projected from here.
   * A turn whose answer never got persisted (a timeout, an empty answer — see
   * backend 9.6/9.7) shifts the projection by one until the next reload reads
   * real seqs back. Only the compaction divider depends on it, so the cost of
   * being off is a divider one turn out of place.
   */
  const nextSeqRef = useRef(FIRST_SEQ);

  // Switching conversation mid-stream must not let the abandoned turn keep
  // writing into the new one. Each request captures a generation; a newer
  // request, a load, or a reset makes every guarded write from the old one a
  // no-op, and aborts its fetch so the reader unwinds.
  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const cancelActiveRequest = useCallback(() => {
    requestIdRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    setIsLoading(false);
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  const sendMessage = useCallback(
    async (userText: string) => {
      const userMessage: ChatMessage = {
        id: v4(),
        role: 'user',
        content: userText,
        toolCalls: [],
        isStreaming: false,
        seq: nextSeqRef.current++,
      };
      const assistantPlaceholder: ChatMessage = {
        id: v4(),
        role: 'assistant',
        content: '',
        toolCalls: [],
        isStreaming: true,
        seq: nextSeqRef.current++,
      };

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const requestId = ++requestIdRef.current;
      const isCurrent = () => requestIdRef.current === requestId;

      setMessages((prev) => [...prev, userMessage, assistantPlaceholder]);
      setIsLoading(true);
      setError(null);

      try {
        const config = configManager.getConfig();
        const baseUrl = `${config?.api.httpProtocol}://${config?.api.url}/api/v1/`;
        const token = await securityClient.getAccessTokenSilently();

        const response = await fetch(`${baseUrl}insights/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
          body: JSON.stringify({
            message: userText,
            ...(conversationIdRef.current && {
              conversationId: conversationIdRef.current,
            }),
          }),
        });

        if (!response.ok || !response.body) {
          throw new Error(`Request failed: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (!isCurrent()) return;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const json = line.slice(6).trim();
            if (!json) continue;
            let event: ChatEvent;
            try {
              event = JSON.parse(json) as ChatEvent;
            } catch {
              continue; // skip malformed SSE lines
            }

            if (event.type === 'error') {
              setError(event.message);
              setMessages(markLastAssistantDone);
              return;
            }
            if (event.type === 'conversation_started') {
              applyConversationId(event.conversationId);
              onCreatedRef.current?.(event.conversationId);
              continue;
            }
            if (event.type === 'context_compacted') {
              setCompactedThroughSeq(event.summarizedThroughSeq);
              continue;
            }
            setMessages((prev) => applyEvent(prev, event));
          }
        }
      } catch (err) {
        if (!isCurrent()) return; // superseded turn — its abort is not an error
        setError((err as Error).message);
        setMessages(markLastAssistantDone);
      } finally {
        if (isCurrent()) {
          abortRef.current = null;
          setIsLoading(false);
        }
      }
    },
    [applyConversationId],
  );

  /** Replace the visible transcript with a conversation fetched from the server. */
  const loadConversation = useCallback(
    (detail: ConversationDetail) => {
      cancelActiveRequest();
      setMessages(
        detail.messages.map((message) => ({
          // Stable across re-renders, and unique across conversations.
          id: `${detail.conversationId}#${message.seq}`,
          role: message.role,
          content: message.content,
          toolCalls: [], // tool-call progress is not persisted server-side
          isStreaming: false,
          seq: message.seq,
        })),
      );
      nextSeqRef.current =
        (detail.messages[detail.messages.length - 1]?.seq ?? FIRST_SEQ - 1) + 1;
      setCompactedThroughSeq(detail.summarizedThroughSeq ?? null);
      setError(null);
      applyConversationId(detail.conversationId);
    },
    [cancelActiveRequest, applyConversationId],
  );

  /** Drop back to an empty chat; the next turn asks the server for a new conversation. */
  const startNewConversation = useCallback(() => {
    cancelActiveRequest();
    setMessages([]);
    nextSeqRef.current = FIRST_SEQ;
    setCompactedThroughSeq(null);
    setError(null);
    applyConversationId(null);
  }, [cancelActiveRequest, applyConversationId]);

  /**
   * First turn the model still sees verbatim — where the "earlier messages
   * summarized" divider goes. Null when nothing has been folded, or when the
   * whole visible transcript sits below the watermark.
   */
  const compactedBeforeMessageId = useMemo(() => {
    if (compactedThroughSeq === null) return null;
    const firstVerbatim = messages.find(
      (message) => message.seq !== undefined && message.seq > compactedThroughSeq,
    );
    return firstVerbatim?.id ?? null;
  }, [messages, compactedThroughSeq]);

  return {
    messages,
    sendMessage,
    isLoading,
    error,
    conversationId,
    compactedThroughSeq,
    compactedBeforeMessageId,
    loadConversation,
    startNewConversation,
  };
}
